import WasmController from "react-lib/frameworks/WasmController";

import ReportCoverageUsage from "issara-sdk/apis/ReportCoverageUsage_apps_CLM";

import { State as MainState } from "HIS/MainHISInterface";
import { downloadXLSX, SetProperty } from "react-lib/apps/HISV3/common/CommonInterface";
import { convertThaiDateToISO } from "react-lib/utils/dateUtils";

export type State = Partial<{
  // sequence
  WellnessSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    filterWellness: FilterWellnessType;
    reportCoverageList: ReportCoverageListType;
  }> | null;
}>;

type FilterWellnessType = {
  start_date?: string;
  end_date?: string;
  product_code?: string;
  invoice_code?: string;
  receipt_code?: string;
  patient_name?: string;
};

type ReportCoverageListType = {
  items: Record<string, any>[];
  activePage: number;
  total: number;
  totalPrice: number;
};

type Picked = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "django" | "searchedItemListWithKey">
>;

// Sequence
type SeqState = {
  sequence: "Wellness";
  restart?: boolean;
  clear?: boolean;
  card?: string;
  filterByDivision?: boolean;
};

// Handle Action
type ActionType =
  // Search
  | { action: "SEARCH"; card?: string; activePage?: number }
  | { action: "DOWNLOAD_XLSX"; card?: string };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => any;

export type SetProp = SetProperty<State & Picked>;

type CustomExtract<T, U> = T extends T ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const DEFAULT_LIST_LIMIT = 10;

export const StateInitial: State = {
  // sequence
  WellnessSequence: {
    sequenceIndex: null,
  },
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
  device?: number;
};

export const BUTTON_ACTIONS = {
  SEARCH: "SEARCH",
  DOWNLOAD_XLSX: "DOWNLOAD_XLSX",
} as const;

type Handler<P = any, R = any> = (
  controller: WasmController<State & Picked, Event, Data, Props>,
  params: P,
) => R;

/* ------------------------------------------------------ */

/*                          START                         */

/* ------------------------------------------------------ */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      WellnessSequence: {
        ...state.WellnessSequence,
        sequenceIndex: "Action",
        filterWellness: {
          start_date: "",
          end_date: "",
          product_code: "",
          invoice_code: "",
          receipt_code: "",
          patient_name: "",
        },
      },
    },
    () =>
      Action(controller, {
        action: "SEARCH",
        card: params.card ?? "",
      }),
  );
};

/* ------------------------------------------------------ */

/*                      Handle Action                     */

/* ------------------------------------------------------ */
export const Action: Handler<ActionType> = async (controller, params) => {
  if (params.action === "SEARCH") {
    HandleSearch(controller, params);
  } else if (params.action === "DOWNLOAD_XLSX") {
    HandleDownloadXLSX(controller, params);
  }
};

const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.card}_${params.action}`]: "LOADING",
    },
  });

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_LIST_LIMIT;

  const data = {
    ...state.WellnessSequence?.filterWellness,
    limit: DEFAULT_LIST_LIMIT,
    offset: offset,
    start_date: convertThaiDateToISO(state.WellnessSequence?.filterWellness?.start_date || ""),
    end_date: convertThaiDateToISO(state.WellnessSequence?.filterWellness?.end_date || ""),
  };

  const [reportCoverageRes, reportCoverageErr, reportCoverageNet] = await ReportCoverageUsage.get({
    params: data,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  if (reportCoverageRes) {
    const totalPrice = (reportCoverageRes?.items || []).reduce((sum, item) => sum + item.price, 0);

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.card}_${params.action}`]: "SUCCESS",
      },
      WellnessSequence: {
        ...state.WellnessSequence,
        reportCoverageList: {
          ...state.WellnessSequence?.reportCoverageList,
          activePage,
          items: reportCoverageRes?.items || [],
          total: reportCoverageRes.total || 0,
          totalPrice: totalPrice,
        },
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.card}_${params.action}`]: "ERROR",
      },
    });
  }
};

const HandleDownloadXLSX: Handler<Params<"DOWNLOAD_XLSX">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.card}_${params.action}`]: "LOADING",
    },
  });

  const reportListFilter = state.WellnessSequence?.filterWellness;

  const data = Object.fromEntries(
    Object.entries({
      ...reportListFilter,
      export_excel: true,
      start_date: convertThaiDateToISO(state.WellnessSequence?.filterWellness?.start_date || ""),
      end_date: convertThaiDateToISO(state.WellnessSequence?.filterWellness?.end_date || ""),
    }).filter(([_, value]) => value !== "" && value != null),
  );

  const [reportCoverageRes, reportCoverageErr, reportCoverageNet] = await ReportCoverageUsage.get({
    params: data,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  if (reportCoverageRes) {
    downloadXLSX(reportCoverageRes, "รายงานการใช้สิทธิ");

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.card}_${params.action}`]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.card}_${params.action}`]: "ERROR",
      },
    });
  }
};
